import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from '../people/keys.ts'
import { tagKeys } from '../tags/keys.ts'
import { importKeys } from './keys.ts'

/**
 * Import hooks. The commit is a background job, so the detail query polls
 * while the job is running and stops the moment it is not — a spinner that
 * never resolves is the worst version of this screen.
 */

export function useImports() {
  const api = useApi()
  return useQuery({
    queryKey: importKeys.list(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.imports.get()
      if (error) throw error
      return data.items
    },
  })
}

export type ImportJob = NonNullable<ReturnType<typeof useImports>['data']>[number]

export function useImport(importId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: importKeys.detail(importId ?? ''),
    enabled: importId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.imports({ id: importId ?? '' }).get()
      if (error) throw error
      return data
    },
    // Poll only while the worker has it.
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 1500 : false),
  })
}

function useImportInvalidation() {
  const queryClient = useQueryClient()
  return (id?: string) => {
    if (id) void queryClient.invalidateQueries({ queryKey: importKeys.detail(id) })
    void queryClient.invalidateQueries({ queryKey: importKeys.list() })
    // An import creates people and can invent tags.
    void queryClient.invalidateQueries({ queryKey: peopleKeys.all() })
    void queryClient.invalidateQueries({ queryKey: tagKeys.all() })
  }
}

export function useUploadImport() {
  const api = useApi()
  const invalidate = useImportInvalidation()
  return useMutation({
    mutationFn: async (body: { filename: string; csv: string }) => {
      const { data, error } = await api.api.v1.imports.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data.id),
  })
}

export type UploadedImport = NonNullable<ReturnType<typeof useUploadImport>['data']>

export function useSetImportMapping(importId: string) {
  const api = useApi()
  const invalidate = useImportInvalidation()
  return useMutation({
    mutationFn: async (body: {
      mapping: Record<string, string | null>
      mode?: 'create-only' | 'update-existing'
      dateFormat?: 'us' | 'iso'
    }) => {
      const { data, error } = await api.api.v1.imports({ id: importId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(importId),
  })
}

export function useDryRunImport(importId: string) {
  const api = useApi()
  const invalidate = useImportInvalidation()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.imports({ id: importId })['dry-run'].post()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(importId),
  })
}

export function useCommitImport(importId: string) {
  const api = useApi()
  const invalidate = useImportInvalidation()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.imports({ id: importId }).commit.post()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(importId),
  })
}
